import React, { useContext } from 'react';

import {
  OptimizelyProvider,
  createInstance,
  enums as optimizelyEnums,
} from '@optimizely/react-sdk';
import clientConfig from '../../client/config';
import { AuthContext } from '../../context/AuthContext';
import { usePageMeta } from '../../hooks';
import useBasePageQuery from '../../hooks/useBasePageQuery';
import { filterByTypename } from '../../utils/graphql';
import isMobile from '../../utils/isMobile';
import { firstArrayItem } from '../../utils/firstArrayItem';

export const CustomOptimizelyProvider: React.FunctionComponent = ({
  children,
}) => {
  const auth = useContext(AuthContext);
  const { pageType } = usePageMeta();
  const isSSR = typeof window === 'undefined';
  const userId = auth?.loggedOutGuid ?? null;
  const basePageData = useBasePageQuery();

  const charities = filterByTypename(
    basePageData.data?.page?.relationships?.beneficiaries?.nodes ?? [],
    'Charity',
  );
  const firstCharity = firstArrayItem(charities);
  const charityId = firstCharity?.legacyId ?? '';
  const countryCode = firstCharity?.countryCode ?? '';
  const hasCharityColourOnFrp = Boolean(
    basePageData.data?.page?.branding?.primaryColor,
  );

  const deviceType = !isSSR ? (isMobile(window) ? 'Mobile' : 'Desktop') : null;

  const attributes = {
    charityId,
    pageType: pageType?.toLocaleLowerCase(),
    hasCharityColourOnFrp,
    deviceType,
    marketCode: countryCode,
  };

  const optimizelyInstance = createInstance({
    sdkKey: clientConfig.optimizelyFullstackKey,
    logLevel: optimizelyEnums.LOG_LEVEL.WARNING,
    eventBatchSize: 10, // wasn't set up and was defaulting to 10 anyway
    eventFlushInterval: 300, // TODO Consider putting back to default after embedded checkout test is complete
  });

  return (
    <OptimizelyProvider
      optimizely={optimizelyInstance}
      isServerSide={isSSR}
      // Due to heavy caching we can only enrol users on the client
      user={{ id: !isSSR ? userId : null, attributes }}
    >
      {children}
    </OptimizelyProvider>
  );
};
